import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";
import { homeServiceApi } from "../homeServiceApi";
import { RootState } from "./store";
import { getServiceImageUrl } from "../../utils/serviceImageUtils";
import { parseServiceCategory, type ServiceCategoryRef } from "../../utils/serviceIconUtils";
import { normalizeGstPercent } from "../../utils/pricingUtils";

// 1. Define interfaces for better type safety
interface Service {
  id: string | number;
  name: string;
  service_name?: string;
  price?: number;
  rating?: number;
  image?: string;
  service_icon?: unknown;
  service_catageory?: ServiceCategoryRef;
  provider?: string;
  /** GST % from API (e.g. 5, 12, 18) */
  gst_percent?: number;
  service_about?: string;
}

interface ServiceState {
  allServices: Service[];
  popularServices: Service[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

interface ApiError {
  message: string;
  // Add other potential error fields
}

// 2. Best Practices: Use a structured initial state
const initialState: ServiceState = {
  allServices: [],
  popularServices: [],
  status: "idle",
  error: null,
};

// 3. Fetch all services — GET /services?area_city=… (customer; area required on deployed API)
export const fetchServices = createAsyncThunk<
  Service[],
  string | undefined,
  { rejectValue: ApiError }
>("service/fetchServices", async (areaCity, { rejectWithValue }) => {
  try {
    const data = await homeServiceApi.getAllServices(areaCity);
    return Array.isArray(data) ? data.map(toSliceService) : [];
  } catch (error: any) {
    if (error.response && error.response.data) {
      return rejectWithValue(error.response.data as ApiError);
    }
    return rejectWithValue({ message: "An unexpected error occurred" });
  }
});

function toSliceService(item: Record<string, unknown>): Service {
  const price = item?.price ?? item?.serivce_pricing;
  const gstRaw = item?.gst_percent ?? item?.gstPercent ?? item?.tax_percent ?? item?.service_gst;
  const gstNum = gstRaw != null && gstRaw !== '' ? Number(gstRaw) : NaN;
  const imageUrl = getServiceImageUrl(item);
  return {
    id: String(item?.id ?? ''),
    name: String(item?.name ?? item?.service_name ?? ''),
    service_name: String(item?.service_name ?? item?.name ?? ''),
    price: typeof price === 'number' ? price : Number(price) || 0,
    rating: Number(item?.rating ?? 0),
    image: imageUrl ?? '',
    service_icon: item?.service_icon,
    service_catageory: parseServiceCategory(item?.service_catageory),
    provider: String(item?.provider ?? ''),
    service_about: String(item?.service_about ?? ''),
    ...(!Number.isNaN(gstNum) ? { gst_percent: normalizeGstPercent(gstNum) } : {}),
  };
}

type FetchPopularArg = number | { limit?: number; areaCity?: string };
export const fetchPopularServices = createAsyncThunk<Service[], FetchPopularArg>(
  "service/fetchPopularServices",
  async (arg) => {
    const limit = typeof arg === 'number' ? arg : (arg?.limit ?? 12);
    const areaCity = typeof arg === 'object' ? arg?.areaCity : undefined;
    try {
      const data = await homeServiceApi.getPopularServices(limit, areaCity);
      return Array.isArray(data) ? data.map(toSliceService) : [];
    } catch {
      return [];
    }
  }
);

const serviceSlice = createSlice({
  name: "service",
  initialState,
  reducers: {
    // 4. Readability: Use PayloadAction for typing actions
    setService: (state, action: PayloadAction<Service[]>) => {
      state.allServices = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServices.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        fetchServices.fulfilled,
        (state, action: PayloadAction<Service[]>) => {
          state.status = "succeeded";
          state.allServices = action.payload;
        }
      )
      .addCase(fetchServices.rejected, (state, action) => {
        state.status = "failed";
        state.allServices = [];
        state.error = action.payload?.message || "Failed to fetch services";
      })
      .addCase(fetchPopularServices.fulfilled, (state, action: PayloadAction<Service[]>) => {
        state.popularServices = action.payload;
      });
  },
});

export const { setService } = serviceSlice.actions;
export default serviceSlice.reducer;

// 6. Performance & Best Practices: Typed and specific selectors
export const selectAllServices = (state: RootState) => state.service.allServices;
export const selectPopularServices = (state: RootState) => state.service.popularServices;
export const selectServiceStatus = (state: RootState) => state.service.status;
export const selectServiceError = (state: RootState) => state.service.error;